import React, { useEffect, useMemo } from "react";
import { Mic, MicOff, RotateCcw, Sparkles } from "lucide-react";
import { useSpeechToText } from "../../hooks/useSpeechToText";
import { parseEnquiryTranscript } from "../../utils/parseEnquiryTranscript";

const EXAMPLE_PHRASE =
  'Try: "Customer name Rahul Sharma, mobile 9876543210, business ABC Traders, city Mumbai, pincode 400001, note needs product demo."';

const VoiceEnquiryPanel = ({ onApplyFields, onSwitchToManual }) => {
  const speech = useSpeechToText({ lang: "en-IN", continuous: true });

  const parsedPreview = useMemo(
    () => parseEnquiryTranscript(speech.liveText),
    [speech.liveText]
  );

  useEffect(() => {
    return () => speech.stop();
  }, [speech.stop]);

  const handleApply = () => {
    if (!speech.liveText.trim()) return;
    const parsed = parseEnquiryTranscript(speech.liveText);
    onApplyFields(parsed, { usedVoice: true });
    speech.stop();
    onSwitchToManual?.();
  };

  const previewItems = [
    { label: "Name", value: parsedPreview.customerName },
    { label: "Mobile", value: parsedPreview.mobile },
    { label: "Email", value: parsedPreview.email },
    { label: "Business", value: parsedPreview.businessName },
    { label: "Address", value: parsedPreview.address },
    { label: "City", value: parsedPreview.city },
    { label: "Pincode", value: parsedPreview.pincode },
    { label: "Source", value: parsedPreview.enquirySource },
    { label: "Note", value: parsedPreview.notes },
  ].filter((item) => item.value);

  return (
    <div className="voice-enquiry-panel">
      <p className="voice-enquiry-panel__hint">
        Tap the mic and speak clearly. We&apos;ll fill the form — you can review before submitting.
      </p>

      {!speech.isSupported && (
        <div className="voice-enquiry-panel__alert" role="alert">
          {speech.error || "Voice input is unavailable here. Switch to Manual to type the enquiry."}
        </div>
      )}

      {speech.error && speech.isSupported && (
        <div className="voice-enquiry-panel__alert voice-enquiry-panel__alert--warn" role="alert">
          {speech.error}
        </div>
      )}

      <div className="voice-enquiry-panel__mic-wrap">
        <button
          type="button"
          className={`voice-enquiry-panel__mic${speech.isListening ? " is-listening" : ""}`}
          onClick={speech.toggle}
          disabled={!speech.isSupported}
          aria-label={speech.isListening ? "Stop listening" : "Start voice input"}
          aria-pressed={speech.isListening}
        >
          {speech.isListening ? (
            <MicOff className="voice-enquiry-panel__mic-icon" strokeWidth={2} />
          ) : (
            <Mic className="voice-enquiry-panel__mic-icon" strokeWidth={2} />
          )}
          <span className="voice-enquiry-panel__mic-ring" aria-hidden="true" />
          <span className="voice-enquiry-panel__mic-ring voice-enquiry-panel__mic-ring--delay" aria-hidden="true" />
        </button>
        <p className="voice-enquiry-panel__status">
          {speech.isListening ? "Listening… tap to stop" : "Tap to speak"}
        </p>
      </div>

      <div className="voice-enquiry-panel__transcript" aria-live="polite">
        <div className="voice-enquiry-panel__transcript-head">
          <span>Live transcript</span>
          {speech.liveText && (
            <button
              type="button"
              className="voice-enquiry-panel__clear"
              onClick={speech.resetTranscript}
              aria-label="Clear transcript"
            >
              <RotateCcw size={14} />
              Clear
            </button>
          )}
        </div>
        <p className="voice-enquiry-panel__transcript-text">
          {speech.liveText || "Your words will appear here…"}
        </p>
      </div>

      {previewItems.length > 0 && (
        <div className="voice-enquiry-panel__preview">
          <div className="voice-enquiry-panel__preview-title">
            <Sparkles size={14} />
            Detected fields
          </div>
          <ul>
            {previewItems.map((item) => (
              <li key={item.label}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="voice-enquiry-panel__example">{EXAMPLE_PHRASE}</p>

      <div className="voice-enquiry-panel__actions">
        <button
          type="button"
          className="voice-enquiry-panel__apply"
          onClick={handleApply}
          disabled={!speech.liveText.trim()}
        >
          Apply to form & review
        </button>
      </div>
    </div>
  );
};

export default VoiceEnquiryPanel;
